import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { FieldProps } from '@rjsf/utils';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import { ServiceModal } from './ServiceModal';
import type { ServiceItem } from './types';
export type { ServiceItem };

// ─── Row components ────────────────────────────────────────────────────────────

interface RowProps {
  item: ServiceItem;
  onDelete: () => void;
  handle?: ReactNode;
  dragStyle?: React.CSSProperties;
  dragRef?: (el: HTMLDivElement | null) => void;
}

function ServiceRow({ item, onDelete, handle, dragStyle, dragRef }: RowProps): ReactElement {
  return (
    <div
      ref={dragRef}
      style={dragStyle}
      className="vtwo:flex vtwo:items-center vtwo:gap-2 vtwo:px-4 vtwo:py-3 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white"
    >
      {handle}
      <span className="vtwo:flex-1 vtwo:text-sm vtwo:text-gray-700 vtwo:truncate vtwo:min-w-0">
        {item.name}
      </span>
      <IconButton size="small" onClick={onDelete} aria-label="Delete service">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

interface SortableRowProps {
  id: string;
  item: ServiceItem;
  onDelete: () => void;
}

function SortableServiceRow({ id, item, onDelete }: SortableRowProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <ServiceRow
      dragRef={setNodeRef}
      dragStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      item={item}
      onDelete={onDelete}
      handle={
        <Box
          {...attributes}
          {...listeners}
          className="vtwo:flex vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
        >
          <DragIndicatorIcon className="vtwo:text-gray-400 vtwo:text-[20px]" />
        </Box>
      }
    />
  );
}

// ─── Field ─────────────────────────────────────────────────────────────────────

export function ServiceListManagerField(props: FieldProps<ServiceItem[]>): ReactElement {
  const { formData, onChange, fieldPathId, uiSchema, schema } = props;
  const items = formData ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const title = (uiSchema?.['ui:title'] as string | undefined) ?? schema.title;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const canReorder = items.length > 1;

  function handleAdd(item: ServiceItem) {
    onChange([...items, item], fieldPathId.path);
  }

  function handleDelete(index: number) {
    onChange(
      items.filter((_, i) => i !== index),
      fieldPathId.path
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === String(active.id));
    const newIndex = items.findIndex((it) => it.id === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(items, oldIndex, newIndex), fieldPathId.path);
  }

  let rows: ReactNode;
  if (canReorder) {
    rows = (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={items.map((it) => it.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableServiceRow
              key={item.id}
              id={item.id}
              item={item}
              onDelete={() => handleDelete(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  } else {
    rows = items.map((item, index) => (
      <ServiceRow
        key={item.id}
        item={item}
        onDelete={() => handleDelete(index)}
      />
    ));
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      {title && (
        <Typography variant="body2" component="p" className="vtwo:font-medium vtwo:text-gray-700">
          {title}
        </Typography>
      )}
      {rows}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        fullWidth
        onClick={() => setModalOpen(true)}
        className="vtwo:normal-case vtwo:text-gray-600 vtwo:border-plus-border vtwo:py-2 vtwo:rounded-lg"
      >
        Add service
      </Button>

      <ServiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={handleAdd}
        organisationId={organisationId}
        selectedIds={items.map((it) => it.id)}
      />
    </Box>
  );
}
